import { ApiError } from '@/lib/api';
import type { FinanceCloseBlocker } from '@/api/generated/types.gen';

/**
 * Extract a human-readable error string from an unknown thrown value.
 *
 * The @hey-api SDK (with `throwOnError: true`) throws the parsed problem body —
 * a plain object `{code, title, detail, status}` — NOT our {@link ApiError}. A
 * naive `String(e)` therefore renders the useless literal `"[object Object]"`.
 * This helper unwraps the server's `detail`/`title`/`message` first.
 *
 * Priority:
 *   1. Our ApiError: use the message field (already formatted by api.ts from
 *      problem.detail / problem.title / statusText).
 *   2. Anything with a non-empty `.detail` or `.title` string (the ProblemJSON
 *      the SDK throws on a non-OK response).
 *   3. Anything with a non-empty `.message` string (Error subclasses, SDK
 *      CancelablePromise errors).
 *   4. Safe string fallback — never "[object Object]".
 */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e != null && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    if (typeof obj['detail'] === 'string' && obj['detail']) return obj['detail'];
    if (typeof obj['title'] === 'string' && obj['title']) return obj['title'];
    if (typeof obj['message'] === 'string' && obj['message']) return obj['message'];
  }
  if (typeof e === 'string' && e) return e;
  return 'An unexpected error occurred. Please try again.';
}

/**
 * The stable machine-readable `code` off a rejection (api/errors/codes.go's
 * `Code` enum on the wire) — same two-shape unwrap as {@link errorStatus} in
 * RouteError.tsx (our hand-rolled ApiError vs the raw Problem JSON object the
 * generated hey-api SDK throws under throwOnError). Distinct from
 * errorMessage: a caller branching on WHICH error this is (e.g. "show a
 * remainder-mode picker, not a generic message" for `remainder_required`)
 * needs the code, not the human sentence in detail/title — string-matching
 * detail would break the moment the wording changes.
 */
export function errorCode(e: unknown): string | undefined {
  if (e instanceof ApiError) return e.code;
  if (e != null && typeof e === 'object') {
    const c = (e as Record<string, unknown>)['code'];
    if (typeof c === 'string' && c) return c;
  }
  return undefined;
}

/**
 * The `blockers` array a `close_blocked` rejection carries (Problem's own
 * extension, api/openapi.yaml — FinanceCloseBlocker[]; §9.1). Same two-shape
 * unwrap as {@link errorCode}: the generated SDK throws the raw Problem
 * object under `throwOnError`, which is where `blockers` lives; ApiError's
 * own `body` is checked too for the rare caller that wrapped one by hand.
 * Any other error (a different code, a network failure) has no blockers, so
 * this returns undefined rather than an empty array — callers can tell "not
 * this kind of error" apart from "blocked with zero reasons" (which the
 * server never actually sends).
 */
export function errorBlockers(e: unknown): FinanceCloseBlocker[] | undefined {
  if (e instanceof ApiError) {
    const body = e.body as { blockers?: FinanceCloseBlocker[] } | undefined;
    return body?.blockers;
  }
  if (e != null && typeof e === 'object') {
    const b = (e as Record<string, unknown>)['blockers'];
    if (Array.isArray(b)) return b as FinanceCloseBlocker[];
  }
  return undefined;
}
